import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrawerModal } from './DrawerModal';
import { usePublic } from '../../../context/PublicContext';
import { getFavoritesIds, toggleFavorite } from '../../../utils/favorites';
import { Heart, Trash2, ShoppingCart, Package } from 'lucide-react';
import { env } from '../../../config/env';
import { addToCart } from '../../../utils/storage';
import { motion, AnimatePresence } from 'framer-motion';

interface FavoritesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${env.IMAGE_BASE_URL}${path.replace(/^\//, '')}`;
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(price);
};

export const FavoritesModal: React.FC<FavoritesModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { products, isLoadingProducts } = usePublic();
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  const refreshFavorites = () => {
    setFavoriteIds(getFavoritesIds());
  };

  useEffect(() => {
    refreshFavorites();
    const handleUpdate = () => refreshFavorites();
    window.addEventListener('favoritesUpdated', handleUpdate);
    return () => window.removeEventListener('favoritesUpdated', handleUpdate);
  }, []);

  const favoriteProducts = products?.filter(p => favoriteIds.includes(p.id)) || [];

  const handleRemoveFavorite = (productId: string) => {
    toggleFavorite(productId);
    refreshFavorites();
  };

  const handleAddToCart = (productId: string) => {
    addToCart(productId, 1);
  };

  if (isLoadingProducts) {
    return (
      <DrawerModal isOpen={isOpen} onClose={onClose} title="Mes favoris">
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        </div>
      </DrawerModal>
    );
  }

  return (
    <DrawerModal isOpen={isOpen} onClose={onClose} title="Mes favoris">
      {favoriteProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <img
            src="/assets/images/illustra/404.svg"
            alt="Aucun favori"
            className="w-60 h-60 object-contain mb-6 opacity-80"
          />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun favori</h3>
          <p className="text-white/70 text-sm mb-8 max-w-xs mx-auto">
            Ajoutez vos produits préférés en cliquant sur le cœur.
          </p>
          <button
            onClick={() => {
              onClose();
              navigate('/boutique');
            }}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-gradient-to-r from-primary to-secondary text-black font-medium hover:scale-105 transition"
          >
            <ShoppingCart size={16} />
            Découvrir la boutique
          </button>
        </div>
      ) : (
        <AnimatePresence mode="popLayout">
          <div className="space-y-4">
            {favoriteProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="flex gap-4 p-3 rounded-xl backdrop-blur-sm bg-white/10 border border-white/20 shadow-md hover:shadow-lg hover:bg-white/15 transition-all duration-200"
              >
                <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-800/30 flex-shrink-0">
                  {product.thumbnail ? (
                    <img
                      src={getImageUrl(product.thumbnail)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={32} className="text-white/40" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className="font-semibold text-white line-clamp-1">{product.name}</h4>
                  <p className="text-sm text-primary font-bold">{formatPrice(product.price)}</p>
                  <button
                    onClick={() => handleAddToCart(product.id)}
                    className="flex items-center gap-1 text-xs bg-white font-medium backdrop-blur-sm px-3 py-1 rounded-full text-primary hover:bg-primary/40 transition"
                  >
                    <ShoppingCart size={12} /> Ajouter au panier
                  </button>
                </div>
                <button
                  onClick={() => handleRemoveFavorite(product.id)}
                  className="text-red-600 hover:text-red-400 transition self-center bg-red-50 rounded-full px-2 py-2"
                >
                  <Trash2 size={18} />
                </button>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      )}
    </DrawerModal>
  );
};